import styles from './index.scss'
import { Button } from 'antd-mobile'
import { useEffect, useMemo, useState } from 'react'
import Layout from '@/libs/components/Layout'
import goTo from '@/libs/functions/goTo'
import { getUrlAllParams, getValue } from '@/libs/functions/getValue'

import { getMiniProgramChannelApi } from '@/libs/functions/appAdapter'
import { getStorage } from '@/libs/functions/onStorage'

import { orderGet, getPaySuccessPageConfig } from '@/services'
import useCategorySet from '@/libs/hooks/useCategorySet'
import { hex2Rgb } from '@/libs/functions/tools'
import { getCustomThemeStyles } from '@/libs/functions/generateCssVars'
import { SvgErrorIcon, SvgSuccessIcon } from '@/libs/components/Icon/svgIcon'
import { useModuleLang } from '@/libs/hooks/useLang'
import { MarketingRecommendList } from '@/libs/components'

// 已知3位品类码集合（orderDetail subreddit 字典里的3位 key）
// 用于从 orderNo 前缀正确提取品类码，避免 GQR/SCG/OCG 等被截断为2位
const THREE_LETTER_CATEGORIES = [
  'GQR',
  'SSC',
  'OCG',
  'SCG',
  'TPS',
  'COP',
  'EXP',
  'MX',
]
function extractCategoryFromOrderNo(orderNo?: string): string {
  if (!orderNo) return ''
  const prefix3 = orderNo.substring(0, 3).toUpperCase()
  if (THREE_LETTER_CATEGORIES.includes(prefix3)) return prefix3
  return orderNo.substring(0, 2).toUpperCase()
}

// 接口返回的中间区域信息项
interface ResultInfoItem {
  label: string
  value: string
}

function Result(props: any) {
  const [payTrans] = useModuleLang(['pay_util'])
  const [limTrans] = useModuleLang(['limousine'])
  const channel = getStorage('channel')
  const channelApi = getMiniProgramChannelApi(channel)
  const [loading, setLoading] = useState(true)
  const [orderDetail, setOrderDetail] = useState<any>({
    orderType: '',
  })
  const [paySuccessConfig, setPaySuccessConfig] = useState<any>({})

  const urlParams = (getUrlAllParams() as any) || {}
  // URL上是否显式传了status字段：有则显示「支付结果」，无则显示「提交成功」
  const hasStatusField = 'status' in urlParams
  let {
    orderNo,
    status = 'success',
    backUrl,
    payResult = '',
    errMsg = '',
  } = urlParams

  if (backUrl) backUrl = decodeURIComponent(backUrl)

  if (errMsg && payResult === '1') {
    switch (errMsg) {
      case 'requestPayment:ok':
        status = 'success'
        break
      case 'requestPayment:fail cancel':
        status = 'cancel'
        break
      default:
        status = 'failure'
    }
  }

  // 页面状态优先以 URL 上的 status 为准；
  // 若 URL 未显式传 status（如 payCallback 链路后端 redirect 回 result 时没带 status），
  // 则等 orderGet 返回后根据订单实际状态判断，避免把支付失败误显示为成功
  const isSuccess = useMemo(() => {
    // LS(礼宾车)：下单即预约成功，与 limousine 自己的 result 页一致，
    // 不按 orderStatus(如 wait_pay) 判定为失败
    if (orderDetail?.orderType === 'LS') return true
    // 1. URL 显式给了 status，直接用
    if (hasStatusField) return status === 'success'
    // 2. URL 没 status，等订单接口返回后判断
    const orderStatus = String(
      orderDetail?.orderStatus ?? orderDetail?.payStatus ?? '',
    ).toLowerCase()
    if (orderStatus) {
      const failureKeywords = [
        'wait_pay',
        'cancel',
        'pending',
        'unpaid',
        'fail',
        'refund_pending',
      ]
      const successKeywords = [
        'finish',
        'success',
        'paid',
        'wait_use',
        'used',
        'complete',
      ]
      if (successKeywords.some((k) => orderStatus.includes(k))) return true
      if (failureKeywords.some((k) => orderStatus.includes(k))) return false
    }
    // 3. 接口也没返回可用状态，默认按 URL 判断（默认值 success）
    return status === 'success'
  }, [
    hasStatusField,
    status,
    orderDetail?.orderType,
    orderDetail?.orderStatus,
    orderDetail?.payStatus,
  ])

  // 页面风格只读租户级配置（TENANT），与收银台/payCallback 保持一致；
  // 不能在订单返回后切换成品类级配置，否则部分租户品类配置缺省会把租户主色覆盖成默认红灰
  const { categorySetting } = useCategorySet(
    'TENANT' as any,
    getValue('tenantCode'),
  )

  const { base, buttonStyle, certificatePage, h5Setting } =
    categorySetting || {}

  // 样式变量：主色、按钮背景、圆角、文字色等
  // 颜色字段在 base 对象里（与 pets.ts 保持一致）
  const styleSetting = useMemo(
    () => ({
      '--primary-color': base?.primaryColor || '#f34242',
      '--primary-background-color': base?.primaryBackgroundColor || '#f5f6f8',
      '--secondary-background-color':
        base?.secondaryBackgroundColor || '#ffffff',
      '--secondary-background-color1':
        base?.secondaryBackgroundColor1 ||
        base?.primaryBackgroundColor ||
        '#f5f6f8',
      '--text-color': base?.textColor || '#222222',
      '--button-radius': (buttonStyle?.borderRadius || 0) + 'px',
      '--button-background-color':
        buttonStyle?.backgroundColorType === 'pure'
          ? buttonStyle?.backgroundColor1 || base?.primaryColor || '#f34242'
          : `linear-gradient(${
              buttonStyle?.backgroundColorType === 'horizontalGradient'
                ? 'to right'
                : 'to bottom'
            }, ${
              buttonStyle?.backgroundColor1 || base?.primaryColor || '#f34242'
            }, ${
              buttonStyle?.backgroundColor2 || base?.primaryColor || '#f34242'
            })`,
      '--button-text-color': buttonStyle?.textColor || '#fff',
      '--btn-border-style': 'none',
    }),
    [base, buttonStyle],
  )

  // 按钮样式（主按钮）
  const primaryButtonStyle = {
    '--border-color': 'none',
    '--background-color': 'var(--button-background-color, #f34242)',
    '--border-radius': 'var(--button-radius)',
    '--text-color': 'var(--button-text-color, #fff)',
    background: 'var(--button-background-color, #f34242)',
    color: 'var(--button-text-color, #fff)',
  } as React.CSSProperties

  // 按钮样式（幽灵/返回首页按钮）
  // antd-mobile Button 用 CSS 变量驱动，必须走 --border-color / --text-color / --background-color
  const ghostButtonStyle = {
    '--border-color': 'var(--primary-color)',
    '--text-color': 'var(--primary-color)',
    '--background-color': 'transparent',
    '--border-radius': 'var(--button-radius)',
    background: 'transparent',
    color: 'var(--primary-color)',
    border: '1px solid var(--primary-color)',
    borderRadius: 'var(--button-radius)',
  } as React.CSSProperties

  // 顶部结果文案：
  // - URL status=success：新接口配置 paySuccessTips 优先，为空按状态取默认值
  // - 其他状态（failure/cancel 等）：不读取任何配置文案，硬编码默认值（防止配置接口返回历史/错误文案导致显示"支付成功"）
  const resultTitle = useMemo(() => {
    // LS(礼宾车)：统一显示「预约成功」，与 limousine result 一致
    if (orderDetail?.orderType === 'LS')
      return limTrans('lim_reserve_success', '预约成功')
    const textMap: any = {
      success: payTrans('paySuccess', '支付成功'),
      cancel: payTrans('payCancel', '支付取消'),
      fail: payTrans('payFailed', '支付失败'),
      failure: payTrans('payFailed', '支付失败'),
    }
    if (status !== 'success') return textMap[status] || textMap.fail
    const apiTitle = paySuccessConfig?.paySuccessTips
    if (apiTitle) return apiTitle
    return textMap[status] || textMap.success
  }, [paySuccessConfig, status, payTrans, limTrans, orderDetail?.orderType])

  // 中间纯提示文案：
  // - URL status=success（含无 status 字段的提交成功页，默认 success）：新接口 paySuccessTips 优先，其次按品类兜底
  // - 其他状态：不读取任何配置，置空（避免中间还冒出一条"下单成功"类文案）
  const middleTipText: string = useMemo(() => {
    // LS(礼宾车)：与 limousine 自己的 result 一致，只显示「预约成功」，不再展示接口配置的中间提示(如"支付成功")
    if (orderDetail?.orderType === 'LS') return ''
    if (status !== 'success') return ''
    // 支付结果页不展示中间描述（paySuccessTips 已用于大标题），与 goods 各品类 result 对齐
    if (hasStatusField) return ''
    const apiTip = paySuccessConfig?.paySuccessTips
    if (apiTip) return apiTip
    const categoryType =
      orderDetail?.orderType ||
      orderDetail?.details?.[0]?.productInfo?.categoryType ||
      extractCategoryFromOrderNo(orderNo)
    const CATEGORY_DEFAULT_TIP: Record<string, string> = {
      JD: '请耐心等待酒店确认',
      CP: '下单成功',
      SCG: '下单成功',
      EXP: '下单成功',
      GV: '提交成功',
    }
    return CATEGORY_DEFAULT_TIP[categoryType] || ''
  }, [status, paySuccessConfig, hasStatusField, orderDetail, orderNo])

  // 中间区域信息列表：
  // - 成功态：接口 infoLists 优先，GV 品类接口未返回时从订单详情提取商品名兜底
  // - 失败态：不读取任何配置/接口返回，置空
  const resultInfoList: ResultInfoItem[] = useMemo(() => {
    if (!isSuccess) return []
    const apiList = paySuccessConfig?.infoLists
    if (Array.isArray(apiList) && apiList.length > 0) {
      return apiList.filter((item) => item?.label || item?.value)
    }
    // 仅GV品类：接口未返回时，从订单详情中提取商品名称，兼容GV自己的结果页逻辑
    const categoryType =
      orderDetail?.orderType ||
      orderDetail?.details?.[0]?.productInfo?.categoryType
    if (categoryType === 'GV') {
      const productName =
        orderDetail?.details?.[0]?.productInfo?.proName ||
        orderDetail?.details?.[0]?.productInfo?.productName
      if (productName) {
        return [{ label: '服务', value: productName }]
      }
    }
    return []
  }, [isSuccess, paySuccessConfig, orderDetail])

  // 礼宾车(LS)订单：从订单详情映射服务明细，用于结果页展示与 limousine LS/result 兼容的中间卡片
  // 仅 orderType==='LS' 时计算；非 LS 订单返回 null，不触发任何 limousine 文案查询
  const lsInfo = useMemo(() => {
    if (orderDetail?.orderType !== 'LS') return null
    const d0 = orderDetail?.details?.[0] || {}
    const serviceInfo = d0.serviceInfo || {}
    const productInfo = d0.productInfo || {}
    const detailObj: Record<string, any> = {}
    try {
      const dl =
        orderDetail?.reservationInfo?.groupInfosList?.[0]?.detailList || []
      dl.forEach((it: any) => {
        if (it?.fieldCode) detailObj[it.fieldCode] = it.value
      })
    } catch (e) {
      /* ignore */
    }
    const serviceTypeMap: Record<string, string> = {
      0: limTrans('lim_service_pick_up', '接机服务'),
      1: limTrans('lim_service_drop_off', '送机服务'),
      2: limTrans('lim_service_pick_station', '接站服务'),
      3: limTrans('lim_service_drop_station', '送站服务'),
      pickUp: limTrans('lim_service_pick_up', '接机服务'),
      dropOff: limTrans('lim_service_drop_off', '送机服务'),
      pickStation: limTrans('lim_service_pick_station', '接站服务'),
      dropStation: limTrans('lim_service_drop_station', '送站服务'),
    }
    const merged = { ...serviceInfo, ...productInfo, ...detailObj }
    // 优先用后端返回的中文服务名 serviceName，其次按枚举/字符串映射，避免直接展示原始值(如 pickUp)
    const serviceTypeText =
      merged.serviceName ||
      merged.serviceTypeDesc ||
      merged.serviceTypeName ||
      serviceTypeMap[merged.serviceType] ||
      merged.serviceType
    return { ...merged, serviceTypeText }
  }, [orderDetail, limTrans])

  // 主按钮：无论支付成功还是失败，统一展示「查看详情」，点击均跳订单详情
  // 成功态文案允许接口配置 viewDetailBtnText 覆盖；失败态不读配置文案，硬编码默认值
  const viewDetailText = isSuccess
    ? paySuccessConfig?.viewDetailBtnText ||
      orderDetail?.viewDetailBtnText ||
      payTrans('viewDetails', '查看详情')
    : payTrans('viewDetails', '查看详情')

  // 次按钮：文案取接口 homePageName 字段；showHomePage 为 false 时不展示
  // （兼容未返回 showHomePage 的旧配置：未返回时仍按 homePageName 是否有值决定是否展示）
  const homePageUrl = paySuccessConfig?.homePageUrl || ''
  const homeBtnText = paySuccessConfig?.homePageName || ''
  const showHomeBtn = !!homeBtnText && paySuccessConfig?.showHomePage !== false

  useEffect(() => {
    setLoading(true)
    if (channelApi && channelApi.goToPayResult) {
      channelApi.goToPayResult({ status })
    }
    // 并行拉取：订单详情 + 支付成功页配置，互不影响
    Promise.allSettled([
      orderGet({ orderNo: orderNo }),
      getPaySuccessPageConfig({ orderNo: orderNo }),
    ])
      .then(([orderResult, configResult]: any[]) => {
        if (orderResult.status === 'fulfilled') {
          const orderRes = orderResult.value
          setOrderDetail(orderRes)
        } else {
          console.error('[Result] orderGet error:', orderResult.reason)
        }
        if (configResult.status === 'fulfilled') {
          const configRes = configResult.value
          setPaySuccessConfig(configRes?.data || configRes || {})
        } else {
          console.error(
            '[Result] paySuccessPageConfig error:',
            configResult.reason,
          )
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [orderNo])

  const onView = () => {
    // backUrl 优先：虚拟商品等场景直接跳到指定结果页，不走订单详情。
    // 仅当 backUrl 是真实路由路径(以 / 开头)才生效；limousine 等链路会传占位
    // 字符串 "backUrl"，必须忽略，否则会跳到 /backUrl 导致 404 空白页。
    // 另外若 backUrl 本身就是 /result（当前页）也跳过，避免在 result 里循环跳回。
    if (backUrl && backUrl.startsWith('/') && !backUrl.startsWith('/result')) {
      goTo(backUrl)
      return
    }
    // 兼容 goods 各品类 result 硬编码 type 的跳转逻辑：
    // 1. 优先取订单接口返回的顶层 orderType（与 details 同级）
    // 2. 兜底取 productInfo.categoryType
    // 3. 从 orderNo 前缀提取（支持 GQR/SCG/OCG 等3位品类码）
    const type =
      orderDetail?.orderType ||
      orderDetail?.details?.[0]?.productInfo?.categoryType ||
      extractCategoryFromOrderNo(orderNo)
    // CP 品类需要额外传 cpSource，保持与 goods common/StandardTemplate result 一致
    const cpSource =
      type === 'CP' ? orderDetail?.details?.[0]?.productInfo?.categoryType : ''
    const target = `/order/orderDetail?orderNo=${orderNo}&type=${type}${
      cpSource ? `&cpSource=${cpSource}` : ''
    }`
    goTo(target)
  }

  const onGoHome = () => {
    if (homePageUrl) {
      goTo(homePageUrl)
    }
  }

  return (
    <Layout
      style={styleSetting}
      title={
        orderDetail?.orderType === 'LS'
          ? limTrans('lim_reserve_success', '预约成功')
          : hasStatusField
          ? payTrans('payResult', '支付结果')
          : payTrans('submitSuccess', '提交成功')
      }
      backArrow={false}
      showHomeBtn={showHomeBtn}
      onHomeBtnClick={onGoHome}
      navBarStyle={{
        background: `var(--primary-background-color)`,
        color: `var(--text-color)`,
      }}
    >
      {!loading && (
        <div className={styles.result}>
          <div className={styles.iconWrap}>
            {isSuccess ? (
              <SvgSuccessIcon
                color="var(--primary-color)"
                backgroundColor="var(--secondary-background-color1)"
              />
            ) : (
              <SvgErrorIcon
                color="var(--primary-color)"
                backgroundColor="var(--secondary-background-color1)"
              />
            )}
          </div>
          {/* 结果文案：URL有status字段(支付结果页)展示；LS 订单也展示「预约成功」 */}
          {(hasStatusField || orderDetail?.orderType === 'LS') && (
            <span className={styles.successText}>{resultTitle}</span>
          )}

          {/* 中间纯提示文案：接口 paySuccessTips 优先，其次按品类兜底（JD→请耐心等待...、CP/SCG/EXP→下单成功） */}
          {middleTipText && (
            <span className={styles.middleTipText}>{middleTipText}</span>
          )}

          {/* 中间信息区域：接口返回，无数据不展示 */}
          {resultInfoList.length > 0 && (
            <div className={styles.infoBox}>
              {resultInfoList.map((item, index) => (
                <div className={styles.row} key={`info-${index}`}>
                  <div className={styles.label}>{item.label}</div>
                  <div className={styles.value}>{item.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* 礼宾车(LS)服务明细卡片：与 limousine LS/result 兼容，值不存在的行不渲染 */}
          {lsInfo && (
            <div className={styles.infoBox}>
              <div className={styles.title}>
                {lsInfo.serviceTypeText || lsInfo.serviceType}
              </div>
              {(lsInfo.carTypeName || lsInfo.specDetail || lsInfo.proName) && (
                <div className={styles.row}>
                  <div className={styles.label}>
                    {limTrans('lim_car_level', '车辆级别')}
                  </div>
                  <div className={styles.value}>
                    {lsInfo.carTypeName || lsInfo.specDetail || lsInfo.proName}
                  </div>
                </div>
              )}
              {(lsInfo.flightNumber ||
                lsInfo.trainNumber ||
                lsInfo.flightNo ||
                lsInfo.trainNo) && (
                <div className={styles.row}>
                  <div className={styles.label}>
                    {limTrans('lim_flight_train_number', '航班号/车次')}
                  </div>
                  <div className={styles.value}>
                    {lsInfo.flightNumber ||
                      lsInfo.trainNumber ||
                      lsInfo.flightNo ||
                      lsInfo.trainNo}
                  </div>
                </div>
              )}
              {lsInfo.serviceTime && (
                <div className={styles.row}>
                  <div className={styles.label}>
                    {limTrans('lim_constant_service_time', '服务时间')}
                  </div>
                  <div className={styles.value}>{lsInfo.serviceTime}</div>
                </div>
              )}
              {(lsInfo.startAddress || lsInfo.startAddressDetail) && (
                <div className={styles.row}>
                  <div className={styles.label}>
                    {limTrans('lim_start_point', '起点')}
                  </div>
                  <div className={styles.value}>
                    {`${lsInfo.startAddress || ''}${
                      lsInfo.startAddressDetail || ''
                    }`}
                  </div>
                </div>
              )}
              {(lsInfo.endAddress || lsInfo.endAddressDetail) && (
                <div className={styles.row}>
                  <div className={styles.label}>
                    {limTrans('lim_end_point', '终点')}
                  </div>
                  <div className={styles.value}>
                    {`${lsInfo.endAddress || ''}${
                      lsInfo.endAddressDetail || ''
                    }`}
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className={`${styles.buttonGroup} ${
              showHomeBtn ? styles.buttonGroupTwo : ''
            }`}
          >
            {/* 返回首页：幽灵按钮，根据配置显示 */}
            {showHomeBtn && (
              <Button
                block
                shape="rounded"
                size="middle"
                className={styles.ghostButton}
                style={ghostButtonStyle}
                onClick={onGoHome}
              >
                {homeBtnText}
              </Button>
            )}

            {/* 查看详情：主按钮 */}
            <Button
              block
              shape="rounded"
              type="submit"
              color="primary"
              size="middle"
              className={styles.primaryButton}
              style={primaryButtonStyle}
              onClick={onView}
            >
              {viewDetailText}
            </Button>
          </div>

          <MarketingRecommendList
            productCode={orderDetail?.details?.[0]?.productInfo?.proCode}
            sceneCode="payResult"
            className={styles.recommendSection}
          />
        </div>
      )}
    </Layout>
  )
}

export default Result
